/**
 * The platform's sidebar / drawer navigation row.
 *
 * A class-builder rather than a component, because the two shells that use it
 * wrap different routers and a component would have to abstract over that for
 * no gain. What matters is that the ACTIVE TREATMENT lives in exactly one place.
 *
 * ## Why this exists
 *
 * It did not, and the two portals drifted into two different answers:
 *
 * - seller (`shared/layout/Sidebar.tsx`): a filled pill — `--sidebar-active-bg`
 *   behind `--sidebar-active-ink`, with a `--pillring` inset hairline.
 * - restaurant (`app/manage/nav.ts`): accent TEXT plus a 3px side indicator and
 *   deliberately no fill, citing `docs/design/design-principles.md`.
 *
 * Both were defensible readings of the design docs, which is precisely the
 * problem — a user moving between the two apps sees two products. The seller's
 * treatment wins because it is the older, more-reviewed surface, and because it
 * satisfies the component-level rule in
 * `docs/design/components-states-motion.md` ("background remains light and
 * neutral", "do NOT use full ACCENT color fills"): `--sidebar-active-bg` is
 * `#f0f0f0` on a white sidebar — a light neutral, not an accent.
 *
 * The 3px start-edge indicator is KEPT alongside the fill. It costs nothing, it
 * is what `components-states-motion.md` asks for, and it gives the active row a
 * second signal that does not depend on colour. `start-0` is the logical
 * property, so it flips with direction without a branch.
 */
export function navRowClass(isActive: boolean): string {
  return [
    "relative flex min-h-[44px] items-center rounded-[var(--radius-nav,10px)] px-3 py-2 ps-4 text-sm transition-colors",
    "before:absolute before:inset-y-1.5 before:start-0 before:w-[3px] before:rounded-full before:content-['']",
    isActive
      ? "bg-[color:var(--sidebar-active-bg)] font-semibold text-[color:var(--sidebar-active-ink)] shadow-[var(--shadow-nav-active,inset_0_0_0_1px_var(--pillring))] before:bg-[color:var(--sidebar-active-ink)]"
      : "font-medium text-[color:var(--sidebar-ink-muted)] before:bg-transparent hover:bg-[color:var(--sidebar-hover-bg)] hover:text-[color:var(--sidebar-ink)]",
  ].join(" ");
}
